import { PostMethods } from './postmethods';
import {
  API_DASH_PREFIX,
  API_PDF_PREFIX,
  API_POPIN_PREFIX,
  API_V2_PREFIX,
  NETWORK_CODE_SERVER_ERROR
} from '../constants';
import ApiObject from './apiobject';
import ApiError from './ApiError';
import Logger from '../util/Logger';

/**
 * Since fetch always returns success even for 400 and 500 HTTP codes, we tell it to throw
 *
 * @param {Response} response
 * @returns {Promise<any>}
 */
const handleError = (response) => {
  if (response.ok) {
    return Promise.resolve(response);
  }

  const error = {
    url: response.url,
    code: response.status,
    statusText: response.statusText,
    timestamp: new Date().toISOString()
  };

  // For 404s, add more specific error message
  if (response.status === 404) {
    error.errorMessage = `Resource not found: ${response.url}`;
    console.error('404 Error:', error);
    throw ApiError(error);
  }

  // For server errors (500+)
  if (response.status >= NETWORK_CODE_SERVER_ERROR) {
    error.errorMessage = `Server error: ${response.statusText}`;
    console.error('Server Error:', error);
    throw ApiError(error);
  }

  // For other errors, try to parse response body
  return response.json()
    .then(body => {
      error.data = body.data;
      error.errorMessage = body.status?.user_message || 
                          body.status?.error_message || 
                          `${response.status}: ${response.statusText}`;
      console.error('API Error:', error);
      throw ApiError(error);
    })
    .catch(e => {
      // If JSON parsing fails, use original error
      error.errorMessage = `${response.status}: ${response.statusText}`;
      console.error('Error Response Parsing Failed:', error);
      throw ApiError(error);
    });
}

// Here are all the little configurations we add with the fetch request
const TIMEOUT_MS = 60000; // 60 second timeout

const defaultInit = (method, isExternalAPI = false) => {
  const init = {
    method: method,
    cache: 'no-cache',
    // Include credentials for internal API calls, omit for external ones
    credentials: isExternalAPI ? 'omit' : 'include',
    // Use CORS for external API calls, same-origin for internal
    mode: isExternalAPI ? 'cors' : 'same-origin',
    headers: {
      'Pragma': 'no-cache',
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    timeout: TIMEOUT_MS
  };
  return init;
};

/**
 * Fire off a call to the raw underlying fetch instance.
 *
 * The returned promise is rejected if the request returns anything >= 400
 * but is otherwise untouched.
 *
 * NOTE: Do not use this directly unless you absolutely have to. You are on your own.
 *
 * @param {string} method
 * @param {string} url
 * @param {object | undefined} payload
 * @returns {Promise<any>}
 */
const call = (method, url, payload = undefined, isExternalAPI = false) => {
  // Determine if this is an external API call
  const isExternal = isExternalAPI || 
    url.startsWith('https://api.openai.com') || 
    url.startsWith('https://api.anthropic.com') || 
    url.startsWith('https://api.cohere.ai') ||
    url.startsWith('https://api.deepseek.com') ||
    url.startsWith('https://api.huggingface.co');
    
  const init = defaultInit(method, isExternal);

  // We attach the payload if it is present
  if (payload) {
    init.body = JSON.stringify(payload);
  }

  // Add timeout handling
  const controller = new AbortController();
  init.signal = controller.signal;
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  return fetch(url, init)
    .then(response => {
      clearTimeout(timeoutId);
      return response;
    })
    .catch(error => {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${TIMEOUT_MS/1000} seconds`);
      }
      throw error;
    })
    .then(handleError);
};

/**
 * Fire off a call to the raw underlying fetch instance.
 *
 * The returned promise is rejected if the request returns anything >= 400
 * but is otherwise untouched.
 *
 * NOTE: Do not use this directly unless you absolutely have to. You are on your own.
 *
 * @param {string} method
 * @param {string} url
 * @param {FormData | undefined} payload
 * @returns {Promise<any>}
 */
const multipart = (method, url, payload = undefined) => {
  const init = defaultInit(method);

  // Strip JSON-specific headers so the browser can automatically add the correct
  // multipart/form-data header (with boundary) when a FormData body is used.
  delete init.headers['Content-Type'];
  delete init.headers['Accept'];

  // Attach payload if present
  if (payload) {
    init.body = payload;
  }
  return fetch(url, init).then(handleError);
};

const multipartApi = {

  /**
   * Fire off a GET request
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @returns {Promise<any>}
   */
  get (url) {
    return multipart(PostMethods.GET, url)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  /**
   * Fire off a POST request with optional payload
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @param {object | undefined} payload
   * @returns {Promise<any>}
   */
  post (url, payload = undefined) {
    return multipart(PostMethods.POST, url, payload)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  /**
   * Fire off a PUT request with optional payload
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @param {object | undefined} payload
   * @returns {Promise<any>}
   */
  put (url, payload = undefined) {
    return multipart(PostMethods.PUT, url, payload)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  }
};

export const api = {

  /**
   * Fire off a GET request
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @returns {Promise<any>}
   */
  get (url) {
    return call(PostMethods.GET, url)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  /**
   * Fire off a POST request with optional payload
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @param {object | undefined} payload
   * @returns {Promise<any>}
   */
  post (url, payload = undefined) {
    return call(PostMethods.POST, url, payload)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  /**
   * Fire off a PUT request with optional payload
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * @param {string} url
   * @param {object | undefined} payload
   * @returns {Promise<any>}
   */
  put (url, payload = undefined) {
    return call(PostMethods.PUT, url, payload)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  /**
   * Fire off a DELETE request with optional payload
   *
   * Will reject if the request returns anything >= 400
   * Else it will return the JSON object directly
   *
   * NOTE: We must use del instead of delete since it is a reserved word
   *
   * @param {string} url
   * @param {object | undefined} payload
   * @returns {Promise<any>}
   */
  del (url, payload = undefined) {
    return call(PostMethods.DELETE, url, payload)
      .then((res) => res.json())
      .then((res) => ApiObject.dataFromJson(res));
  },

  raw (method, url, payload = undefined) {
    return call(method, url, payload);
  }
};

export const popinMultipartApi = {

  get (url) {
    return multipartApi.get(`${API_POPIN_PREFIX}${url}`);
  },

  post (url, payload = undefined) {
    return multipartApi.post(`${API_POPIN_PREFIX}${url}`, payload);
  },

  put (url, payload = undefined) {
    return multipartApi.put(`${API_POPIN_PREFIX}${url}`, payload);
  }

};

export const popinApi = {

  get (url) {
    return api.get(`${API_POPIN_PREFIX}${url}`);
  },

  post (url, payload = undefined) {
    return api.post(`${API_POPIN_PREFIX}${url}`, payload);
  },

  put (url, payload = undefined) {
    return api.put(`${API_POPIN_PREFIX}${url}`, payload);
  },

  del (url, payload = undefined) {
    return api.del(`${API_POPIN_PREFIX}${url}`, payload);
  },

  raw (method, url, payload = undefined) {
    return api.raw(method, `${API_POPIN_PREFIX}${url}`, payload);
  }

};

export const printApi = {
  get (url) {
    return api.get(`${API_PDF_PREFIX}${url}`);
  },
  post (url, payload = undefined) {
    return api.post(`${API_PDF_PREFIX}${url}`, payload);
  }
};

export const dashApi = {

  get (url) {
    return api.get(`${API_DASH_PREFIX}${url}`);
  },

  post (url, payload = undefined) {
    return api.post(`${API_DASH_PREFIX}${url}`, payload);
  },

  put (url, payload = undefined) {
    return api.put(`${API_DASH_PREFIX}${url}`, payload);
  },

  del (url, payload = undefined) {
    return api.del(`${API_DASH_PREFIX}${url}`, payload);
  },

  raw (method, url, payload = undefined) {
    return api.raw(method, `${API_DASH_PREFIX}${url}`, payload);
  }

};

export const popinApiV2 = {

  get (url) {
    return api.get(`${API_V2_PREFIX}${url}`);
  },

  post (url, payload = undefined) {
    return api.post(`${API_V2_PREFIX}${url}`, payload);
  },

  put (url, payload = undefined) {
    return api.put(`${API_V2_PREFIX}${url}`, payload);
  },

  del (url, payload = undefined) {
    return api.del(`${API_V2_PREFIX}${url}`, payload);
  },

  raw (method, url, payload = undefined) {
    return api.raw(method, `${API_V2_PREFIX}${url}`, payload);
  }

};
